import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    getCreditBureauSettings,
    saveCreditBureauSettings,
    CreditBureauSettings,
    XdsDataMode,
    getCreditPolicySettings,
    saveCreditPolicySettings,
    CreditPolicySettings,
} from '../../../services/settingsService';
import NotificationSettingsPanel from './NotificationSettingsPanel';
import StatementIqSettingsPanel from './StatementIqSettingsPanel';
import CollateralSettingsPanel from './CollateralSettingsPanel';

interface SettingsSection {
    id: string;
    title: string;
    icon: string;
    description: string;
    fields: {
        id: string;
        label: string;
        type: string;
        value: string | boolean;
        options?: string[];
        description?: string;
    }[];
}

interface DataModeOption {
    value: XdsDataMode;
    label: string;
    description: string;
    icon: string;
    badge?: string;
    badgeVariant?: string;
}

const DATA_MODE_OPTIONS: DataModeOption[] = [
    {
        value: 'LIVE',
        label: 'Live Call',
        description: 'Always fetch fresh data directly from the credit bureau for every assessment. Most accurate but uses more bureau credits.',
        icon: 'fa-bolt',
        badge: 'Real-time',
        badgeVariant: 'success',
    },
    {
        value: 'CACHE_1H',
        label: 'One Hour History',
        description: 'Reuse bureau data fetched within the last hour for the same applicant. Suitable for same-session re-assessments.',
        icon: 'fa-clock-o',
    },
    {
        value: 'CACHE_1D',
        label: 'One Day History',
        description: 'Reuse bureau data fetched within the last 24 hours. Good for applications processed on the same business day.',
        icon: 'fa-calendar',
        badge: 'Recommended',
        badgeVariant: 'primary',
    },
    {
        value: 'CACHE_1W',
        label: 'One Week History',
        description: 'Reuse bureau data fetched within the last 7 days. Balances freshness and cost for standard processing times.',
        icon: 'fa-calendar-o',
    },
    {
        value: 'CACHE_1M',
        label: 'One Month History',
        description: 'Reuse bureau data fetched within the last 30 days. Suitable for returning applicants in stable financial positions.',
        icon: 'fa-calendar-check-o',
    },
    {
        value: 'CACHE_3M',
        label: 'Three Months History',
        description: 'Reuse bureau data fetched within the last 3 months. Best used for low-risk, repeat customers.',
        icon: 'fa-history',
    },
    {
        value: 'CACHE_6M',
        label: 'Six Months History',
        description: 'Reuse bureau data fetched within the last 6 months. Maximum cost savings — suitable for existing customers only.',
        icon: 'fa-database',
        badge: 'Most Economical',
        badgeVariant: 'warning',
    },
];

const VALID_SECTIONS = ['general', 'loan', 'notifications', 'security', 'credit_bureau', 'credit_policy', 'statementiq'];

// What the page banner says for each section.
const SECTION_HEADERS: Record<string, { title: string; description: string }> = {
    general: { title: 'General Settings', description: 'Basic system configuration settings' },
    loan: { title: 'Loan Settings', description: 'Configure loan-related parameters' },
    notifications: { title: 'Notification Settings', description: 'Where emails and SMSs go, and which ones are sent' },
    security: { title: 'Security Settings', description: 'Configure security and access control settings' },
    credit_bureau: { title: 'Credit Bureau Settings', description: 'How bureau data is retrieved from XDS during credit assessments' },
    credit_policy: { title: 'Credit Policy Settings', description: 'Which risk bands map to each credit decision outcome' },
    statementiq: { title: 'StatementIQ Settings', description: 'Enable or disable bank statement analysis' },
};

const Settings = () => {
    // The section is driven by the URL (/settings/:section) so the sidebar can link
    // straight to one, the browser back button works, and a section can be shared as a
    // link. /settings on its own still opens General.
    const { section } = useParams();
    const initialTab = section && VALID_SECTIONS.includes(section) ? section : 'general';
    const [activeTab, setActiveTab] = useState(initialTab);

    // Follow the URL when it changes (sidebar click, back button). An unknown section
    // falls back to General rather than rendering an empty page.
    useEffect(() => {
        const next = section && VALID_SECTIONS.includes(section) ? section : 'general';
        setActiveTab(next);
    }, [section]);



    // Credit Bureau Settings state
    const [creditBureauSettings, setCreditBureauSettings] = useState<CreditBureauSettings>({
        enabled: true,
        dataMode: 'LIVE',
    });
    const [isSavingCreditBureau, setIsSavingCreditBureau] = useState(false);
    const [isLoadingCreditBureau, setIsLoadingCreditBureau] = useState(false);

    // Credit Policy Settings state
    const [creditPolicySettings, setCreditPolicySettings] = useState<CreditPolicySettings>({
        approvedMaxBand: 3,
        referMaxBand: 6,
    });
    const [isSavingCreditPolicy, setIsSavingCreditPolicy] = useState(false);
    const [isLoadingCreditPolicy, setIsLoadingCreditPolicy] = useState(false);

    useEffect(() => {
        if (activeTab === 'credit_bureau') {
            setIsLoadingCreditBureau(true);
            getCreditBureauSettings()
                .then(setCreditBureauSettings)
                .finally(() => setIsLoadingCreditBureau(false));
        }
        if (activeTab === 'credit_policy') {
            setIsLoadingCreditPolicy(true);
            getCreditPolicySettings()
                .then(setCreditPolicySettings)
                .finally(() => setIsLoadingCreditPolicy(false));
        }
    }, [activeTab]);

    const handleSaveCreditBureau = async () => {
        setIsSavingCreditBureau(true);
        try {
            await saveCreditBureauSettings(creditBureauSettings);
            toast.success('Credit Bureau settings saved successfully');
        } catch {
            toast.error('Failed to save Credit Bureau settings');
        } finally {
            setIsSavingCreditBureau(false);
        }
    };

    const handleSaveCreditPolicy = async () => {
        setIsSavingCreditPolicy(true);
        try {
            await saveCreditPolicySettings(creditPolicySettings);
            toast.success('Credit Policy settings saved successfully');
        } catch {
            toast.error('Failed to save Credit Policy settings');
        } finally {
            setIsSavingCreditPolicy(false);
        }
    };

    const settingsSections: SettingsSection[] = [
        {
            id: 'general',
            title: 'General Settings',
            icon: 'fa-gear',
            description: 'Basic system configuration settings',
            fields: [
                {
                    id: 'companyName',
                    label: 'Company Name',
                    type: 'text',
                    value: 'Tirhelo Admin',
                    description: 'The name of your company as it appears throughout the system',
                },
                {
                    id: 'emailFrom',
                    label: 'System Email Address',
                    type: 'email',
                    value: 'noreply@tirhelofin.co.za',
                    description: 'The email address used for system notifications',
                },
                {
                    id: 'timezone',
                    label: 'Default Timezone',
                    type: 'select',
                    value: 'Africa/Johannesburg',
                    options: ['Africa/Johannesburg', 'UTC', 'Europe/London'],
                    description: 'The default timezone for date and time displays',
                },
            ],
        },
        {
            id: 'loan',
            title: 'Loan Settings',
            icon: 'fa-money',
            description: 'Configure loan-related parameters',
            fields: [
                {
                    id: 'maxLoanAmount',
                    label: 'Maximum Loan Amount',
                    type: 'number',
                    value: '500000',
                    description: 'The maximum amount that can be requested for a loan',
                },
                {
                    id: 'defaultInterestRate',
                    label: 'Default Interest Rate (%)',
                    type: 'number',
                    value: '15',
                    description: 'The default interest rate applied to loans',
                },
                {
                    id: 'loanTermLimit',
                    label: 'Maximum Loan Term (months)',
                    type: 'number',
                    value: '60',
                    description: 'The maximum duration for loan repayment',
                },
            ],
        },
        {
            id: 'security',
            title: 'Security Settings',
            icon: 'fa-shield',
            description: 'Configure security and access control settings',
            fields: [
                {
                    id: 'passwordPolicy',
                    label: 'Password Policy',
                    type: 'select',
                    value: 'strong',
                    options: ['basic', 'medium', 'strong'],
                    description: 'Set the required strength for user passwords',
                },
                {
                    id: 'sessionTimeout',
                    label: 'Session Timeout (minutes)',
                    type: 'number',
                    value: '30',
                    description: 'How long until inactive users are logged out',
                },
                {
                    id: 'twoFactorAuth',
                    label: 'Two-Factor Authentication',
                    type: 'checkbox',
                    value: true,
                    description: 'Require two-factor authentication for all users',
                },
            ],
        },
    ];

    // Risk band metadata — bad rates from credit-policy-api ref tables
    const BAND_BAD_RATES: Record<number, number> = {
        1: 0, 2: 0, 3: 0, 4: 0, 5: 18.18, 6: 18.18,
        7: 33.33, 8: 45.45, 9: 63.64, 10: 63.64,
    };
    const ALL_BANDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

    const getBandOutcome = (band: number): 'approved' | 'refer' | 'rejected' => {
        if (band <= creditPolicySettings.approvedMaxBand) return 'approved';
        if (band <= creditPolicySettings.referMaxBand) return 'refer';
        return 'rejected';
    };

    const OUTCOME_CONFIG = {
        approved: { label: 'Approved', color: '#198754', bg: '#d1e7dd', icon: 'fa-check-circle' },
        refer: { label: 'Refer to Credit Team', color: '#fd7e14', bg: '#ffe5d0', icon: 'fa-users' },
        rejected: { label: 'Rejected', color: '#dc3545', bg: '#f8d7da', icon: 'fa-times-circle' },
    };

    const renderCreditPolicySettings = () => {
        if (isLoadingCreditPolicy) {
            return (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            );
        }

        const { approvedMaxBand, referMaxBand } = creditPolicySettings;

        const outcomes = [
            { key: 'approved' as const, from: 1, to: approvedMaxBand },
            { key: 'refer' as const, from: approvedMaxBand + 1, to: referMaxBand },
            { key: 'rejected' as const, from: referMaxBand + 1, to: 10 },
        ];

        return (
            <div>
                <h5 className="mb-1">Credit Policy Settings</h5>
                <p className="text-muted mb-4">
                    Configure which risk bands map to each credit decision outcome.
                    Bands run from R1 (lowest risk) to R10 (highest risk).
                </p>

                {/* Band spectrum bar */}
                <div className="mb-4">
                    <h6 className="fw-semibold mb-2">Band Distribution Preview</h6>
                    <div className="d-flex rounded overflow-hidden" style={{ height: 40 }}>
                        {ALL_BANDS.map(band => {
                            const outcome = getBandOutcome(band);
                            const cfg = OUTCOME_CONFIG[outcome];
                            return (
                                <div
                                    key={band}
                                    className="d-flex align-items-center justify-content-center fw-bold"
                                    style={{
                                        flex: 1,
                                        backgroundColor: cfg.color,
                                        color: '#fff',
                                        fontSize: '0.8rem',
                                        transition: 'background-color 0.2s',
                                    }}
                                    title={`R${band} — ${cfg.label}`}
                                >
                                    R{band}
                                </div>
                            );
                        })}
                    </div>
                    <div className="d-flex mt-2 gap-3">
                        {Object.entries(OUTCOME_CONFIG).map(([key, cfg]) => (
                            <div key={key} className="d-flex align-items-center gap-1">
                                <div className="rounded" style={{ width: 12, height: 12, backgroundColor: cfg.color }}></div>
                                <small className="text-muted">{cfg.label}</small>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Threshold controls */}
                <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body px-4 py-3">
                        <h6 className="fw-semibold mb-3">Band Thresholds</h6>
                        <div className="row g-4 align-items-end">
                            <div className="col-12 col-md-5">
                                <label className="form-label fw-semibold" style={{ color: OUTCOME_CONFIG.approved.color }}>
                                    <i className={`fa ${OUTCOME_CONFIG.approved.icon} me-2`}></i>
                                    Approved up to Band
                                </label>
                                <select
                                    className="form-select"
                                    value={approvedMaxBand}
                                    onChange={e => {
                                        const val = Number(e.target.value);
                                        setCreditPolicySettings(prev => ({
                                            approvedMaxBand: val,
                                            referMaxBand: Math.max(prev.referMaxBand, val + 1),
                                        }));
                                    }}
                                >
                                    {ALL_BANDS.slice(0, 8).map(b => (
                                        <option key={b} value={b}>R{b}</option>
                                    ))}
                                </select>
                                <div className="form-text">R1 — R{approvedMaxBand} = Approved</div>
                            </div>

                            <div className="col-12 col-md-2 text-center pb-4">
                                <i className="fa fa-arrow-right text-muted" style={{ fontSize: '1.2rem' }}></i>
                            </div>

                            <div className="col-12 col-md-5">
                                <label className="form-label fw-semibold" style={{ color: OUTCOME_CONFIG.refer.color }}>
                                    <i className={`fa ${OUTCOME_CONFIG.refer.icon} me-2`}></i>
                                    Refer up to Band
                                </label>
                                <select
                                    className="form-select"
                                    value={referMaxBand}
                                    onChange={e => {
                                        const val = Number(e.target.value);
                                        setCreditPolicySettings(prev => ({
                                            ...prev,
                                            referMaxBand: val,
                                        }));
                                    }}
                                >
                                    {ALL_BANDS.slice(approvedMaxBand, 9).map(b => (
                                        <option key={b} value={b}>R{b}</option>
                                    ))}
                                </select>
                                <div className="form-text">R{approvedMaxBand + 1} — R{referMaxBand} = Refer &nbsp;|&nbsp; R{referMaxBand + 1} — R10 = Rejected</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Outcome table */}
                <div className="card border-0 shadow-sm mb-4">
                    <div className="card-header bg-white py-3 px-4">
                        <h6 className="card-title mb-0 fw-semibold">Band to Outcome Mapping</h6>
                    </div>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="px-4 py-3">Risk Band</th>
                                        <th className="py-3">Expected Bad Rate</th>
                                        <th className="py-3">Risk Profile</th>
                                        <th className="py-3">Outcome</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ALL_BANDS.map(band => {
                                        const outcome = getBandOutcome(band);
                                        const cfg = OUTCOME_CONFIG[outcome];
                                        const badRate = BAND_BAD_RATES[band];
                                        const riskProfile = badRate === 0 ? 'Low' : badRate < 20 ? 'Medium' : badRate < 50 ? 'High' : 'Very High';
                                        const riskBadge = badRate === 0 ? 'success' : badRate < 20 ? 'warning' : badRate < 50 ? 'danger' : 'dark';
                                        return (
                                            <tr key={band} style={{ borderLeft: `3px solid ${cfg.color}` }}>
                                                <td className="px-4 fw-bold" style={{ color: cfg.color }}>R{band}</td>
                                                <td>{badRate === 0 ? '—' : `${badRate}%`}</td>
                                                <td>
                                                    <span className={`badge bg-${riskBadge}`}>{riskProfile}</span>
                                                </td>
                                                <td>
                                                    <span
                                                        className="badge d-inline-flex align-items-center gap-1 px-2 py-1"
                                                        style={{ backgroundColor: cfg.color }}
                                                    >
                                                        <i className={`fa ${cfg.icon}`}></i>
                                                        {cfg.label}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="d-flex align-items-center gap-3">
                    <button
                        type="button"
                        className="btn btn-primary px-4"
                        onClick={handleSaveCreditPolicy}
                        disabled={isSavingCreditPolicy}
                    >
                        {isSavingCreditPolicy ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-save me-2"></i>
                                Save Changes
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setCreditPolicySettings({ approvedMaxBand: 3, referMaxBand: 6 })}
                        disabled={isSavingCreditPolicy}
                    >
                        Reset to Defaults
                    </button>
                </div>
            </div>
        );
    };

    const renderField = (field: SettingsSection['fields'][0]) => {
        switch (field.type) {
            case 'text':
            case 'email':
            case 'number':
                return (
                    <input
                        type={field.type}
                        className="form-control"
                        id={field.id}
                        defaultValue={field.value as string}
                    />
                );
            case 'select':
                return (
                    <select className="form-select" id={field.id} defaultValue={field.value as string}>
                        {field.options?.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                );
            case 'checkbox':
                return (
                    <div className="form-check form-switch">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id={field.id}
                            defaultChecked={field.value as boolean}
                        />
                    </div>
                );
            case 'multiselect':
                return (
                    <select className="form-select" id={field.id} multiple defaultValue={(field.value as string).split(',')}>
                        {field.options?.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                );
            default:
                return null;
        }
    };

    const renderCreditBureauSettings = () => {
        if (isLoadingCreditBureau) {
            return (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            );
        }

        return (
            <div>
                <h5 className="mb-1">Credit Bureau Settings</h5>
                <p className="text-muted mb-4">
                    Configure how the system retrieves credit bureau data from XDS during credit assessments.
                </p>

                {/* Enable / Disable toggle */}
                <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body d-flex align-items-center justify-content-between py-3 px-4">
                        <div>
                            <h6 className="mb-1 fw-semibold">XDS Bureau Integration</h6>
                            <small className="text-muted">
                                When enabled, credit assessments will automatically pull bureau data from XDS.
                            </small>
                        </div>
                        <div className="form-check form-switch ms-4">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="xdsEnabled"
                                checked={creditBureauSettings.enabled}
                                onChange={e =>
                                    setCreditBureauSettings(prev => ({ ...prev, enabled: e.target.checked }))
                                }
                                style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
                            />
                        </div>
                    </div>
                </div>

                {/* Data Mode selector */}
                <div className={creditBureauSettings.enabled ? '' : 'opacity-50 pe-none'}>
                    <h6 className="fw-semibold mb-1">Data Retrieval Mode</h6>
                    <p className="text-muted small mb-3">
                        Choose how often the system should fetch new bureau data for an applicant.
                        Cached modes reduce bureau costs by reusing recent results within the selected window.
                    </p>

                    <div className="row g-3">
                        {DATA_MODE_OPTIONS.map(option => {
                            const isSelected = creditBureauSettings.dataMode === option.value;
                            return (
                                <div key={option.value} className="col-12 col-md-6 col-xl-4">
                                    <div
                                        className={`card h-100 cursor-pointer border-2 ${isSelected ? 'border-primary bg-primary bg-opacity-10' : 'border'}`}
                                        style={{ cursor: 'pointer', transition: 'border-color 0.15s, background-color 0.15s' }}
                                        onClick={() =>
                                            creditBureauSettings.enabled &&
                                            setCreditBureauSettings(prev => ({ ...prev, dataMode: option.value }))
                                        }
                                    >
                                        <div className="card-body p-3">
                                            <div className="d-flex align-items-start justify-content-between mb-2">
                                                <div className="d-flex align-items-center gap-2">
                                                    <div
                                                        className={`rounded-circle d-flex align-items-center justify-content-center ${isSelected ? 'bg-primary text-white' : 'bg-light text-secondary'}`}
                                                        style={{ width: 36, height: 36, flexShrink: 0 }}
                                                    >
                                                        <i className={`fa ${option.icon}`}></i>
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0 fw-semibold" style={{ fontSize: '0.9rem' }}>
                                                            {option.label}
                                                        </h6>
                                                        {option.badge && (
                                                            <span className={`badge bg-${option.badgeVariant} mt-1`} style={{ fontSize: '0.7rem' }}>
                                                                {option.badge}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div
                                                    className={`rounded-circle border-2 mt-1 ${isSelected ? 'border-primary bg-primary' : 'border'}`}
                                                    style={{
                                                        width: 18,
                                                        height: 18,
                                                        flexShrink: 0,
                                                        border: isSelected ? '2px solid var(--bs-primary)' : '2px solid #dee2e6',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    {isSelected && (
                                                        <div
                                                            className="rounded-circle bg-white"
                                                            style={{ width: 8, height: 8 }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <p className="text-muted mb-0" style={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                                                {option.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="mt-4 d-flex align-items-center gap-3">
                    <button
                        type="button"
                        className="btn btn-primary px-4"
                        onClick={handleSaveCreditBureau}
                        disabled={isSavingCreditBureau}
                    >
                        {isSavingCreditBureau ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-save me-2"></i>
                                Save Changes
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setCreditBureauSettings({ enabled: true, dataMode: 'LIVE' })}
                        disabled={isSavingCreditBureau}
                    >
                        Reset to Defaults
                    </button>
                    {creditBureauSettings.enabled && (
                        <span className="text-muted small ms-auto">
                            <i className="fa fa-info-circle me-1"></i>
                            Current mode: <strong>{DATA_MODE_OPTIONS.find(o => o.value === creditBureauSettings.dataMode)?.label}</strong>
                        </span>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div>
            {/* Page Header - the same green banner as Communication */}
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item">
                                    <Link className="text-white" to="/"><i className="fa fa-home me-2"></i>Home</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link className="text-white" to="/settings">Settings</Link>
                                </li>
                                <li className="breadcrumb-item active text-white" aria-current="page">
                                    {SECTION_HEADERS[activeTab]?.title}
                                </li>
                            </ol>
                        </div>
                    </div>
                    <div className="row align-items-center">
                        <div className="col-md-8">
                            <h1 className="fs-2 fw-bold mb-2 text-white">{SECTION_HEADERS[activeTab]?.title}</h1>
                            <p className="mb-0 text-white fs-6 opacity-75">{SECTION_HEADERS[activeTab]?.description}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="row">
                                        {/* Settings Content.
                                            The in-page section list that used to sit here has been
                                            removed: the sidebar's collapsible Settings menu lists the
                                            same six sections, and showing both put the identical list
                                            on screen twice. The sidebar is now the only way to switch
                                            section, and the section comes from the /settings/:section
                                            URL, so a direct link or a refresh still lands correctly. */}
                                        <div className="col-12">
                                            {/* Generic sections */}
                                            {settingsSections.map((section) => (
                                                <div key={section.id} className={activeTab === section.id ? '' : 'd-none'}>
                                                    {/* Saved to the loan API (finding #29); the fields below are not saved yet */}
                                                    {section.id === 'loan' && <CollateralSettingsPanel />}
                                                    <h5 className="mb-3">{section.title}</h5>
                                                    <p className="text-muted mb-4">{section.description}</p>
                                                    <form>
                                                        {section.fields.map((field) => (
                                                            <div key={field.id} className="mb-4">
                                                                <label htmlFor={field.id} className="form-label">
                                                                    {field.label}
                                                                </label>
                                                                {renderField(field)}
                                                                {field.description && (
                                                                    <div className="form-text text-muted">
                                                                        {field.description}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                        <div className="mt-4">
                                                            <button type="submit" className="btn btn-primary me-2">
                                                                Save Changes
                                                            </button>
                                                            <button type="button" className="btn btn-outline-secondary">
                                                                Reset
                                                            </button>
                                                        </div>
                                                    </form>
                                                </div>
                                            ))}

                                            {/* Notification Settings - saved to the notification service */}
                                            {activeTab === 'notifications' && <NotificationSettingsPanel />}

                                            {/* Credit Bureau Settings */}
                                            {activeTab === 'credit_bureau' && renderCreditBureauSettings()}

                                            {/* Credit Policy Settings */}
                                            {activeTab === 'credit_policy' && renderCreditPolicySettings()}
                                            {activeTab === 'statementiq' && <StatementIqSettingsPanel />}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;
